import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface FilterCondition {
  metric: string;
  operator: string;
  value: number;
}

function useIdListState(initial: string[] = []) {
  const [ids, setIds] = useState<string[]>(initial);

  const addId = useCallback((id: string) => {
    setIds(prev => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const removeId = useCallback((id: string) => {
    setIds(prev => prev.filter(existingId => existingId !== id));
  }, []);

  const clearIds = useCallback(() => {
    setIds([]);
  }, []);

  const setUniqueIds = useCallback((newIds: string[]) => {
    setIds([...new Set(newIds)]);
  }, []);

  return { ids, setUniqueIds, addId, removeId, clearIds };
}

interface InsightFiltersContextType {
  campaignIds: string[];
  setCampaignIds: (ids: string[]) => void;
  addCampaignId: (id: string) => void;
  removeCampaignId: (id: string) => void;
  clearCampaignIds: () => void;

  adGroupIds: string[];
  setAdGroupIds: (ids: string[]) => void;
  addAdGroupId: (id: string) => void;
  removeAdGroupId: (id: string) => void;
  clearAdGroupIds: () => void;

  keywordIds: string[];
  setKeywordIds: (ids: string[]) => void;
  addKeywordId: (id: string) => void;
  removeKeywordId: (id: string) => void;
  clearKeywordIds: () => void;

  targetingIds: string[];
  setTargetingIds: (ids: string[]) => void;
  addTargetingId: (id: string) => void;
  removeTargetingId: (id: string) => void;
  clearTargetingIds: () => void;

  metricFilters: FilterCondition[];
  setMetricFilters: (filters: FilterCondition[]) => void;
  clearAllFilters: () => void;
}

const InsightFiltersContext = createContext<InsightFiltersContextType | undefined>(undefined);

export function InsightFiltersProvider({ children }: { children: ReactNode }) {
  const campaign = useIdListState();
  const adGroup = useIdListState();
  const keyword = useIdListState();
  const targeting = useIdListState();
  const [metricFilters, setMetricFilters] = useState<FilterCondition[]>([]);

  const clearAllFilters = useCallback(() => {
    campaign.clearIds();
    adGroup.clearIds();
    keyword.clearIds();
    targeting.clearIds();
    setMetricFilters([]);
  }, [campaign, adGroup, keyword, targeting]);

  return (
    <InsightFiltersContext.Provider
      value={{
        campaignIds: campaign.ids,
        setCampaignIds: campaign.setUniqueIds,
        addCampaignId: campaign.addId,
        removeCampaignId: campaign.removeId,
        clearCampaignIds: campaign.clearIds,

        adGroupIds: adGroup.ids,
        setAdGroupIds: adGroup.setUniqueIds,
        addAdGroupId: adGroup.addId,
        removeAdGroupId: adGroup.removeId,
        clearAdGroupIds: adGroup.clearIds,

        keywordIds: keyword.ids,
        setKeywordIds: keyword.setUniqueIds,
        addKeywordId: keyword.addId,
        removeKeywordId: keyword.removeId,
        clearKeywordIds: keyword.clearIds,

        targetingIds: targeting.ids,
        setTargetingIds: targeting.setUniqueIds,
        addTargetingId: targeting.addId,
        removeTargetingId: targeting.removeId,
        clearTargetingIds: targeting.clearIds,

        metricFilters,
        setMetricFilters,
        clearAllFilters,
      }}
    >
      {children}
    </InsightFiltersContext.Provider>
  );
}

export function useInsightFilters() {
  const context = useContext(InsightFiltersContext);
  if (context === undefined) {
    throw new Error('useInsightFilters must be used within an InsightFiltersProvider');
  }
  return context;
}

// Keep the old hook name for backward compatibility if needed
export const useInsightCampaignIds = useInsightFilters;
